'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type Language = 'it' | 'en';

type TranslationValue = string | Record<string, unknown>;
type Translations = Record<string, TranslationValue>;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, fallback?: string) => string;
  translations: Translations;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'it',
  setLanguage: () => {},
  t: (key) => key,
  translations: {},
});

const LANG_KEY = 'lcm_language';

function getNestedValue(obj: Record<string, unknown>, path: string): string | undefined {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>('it');
  const [translations, setTranslations] = useState<Translations>({});

  const loadTranslations = useCallback(async (lang: Language) => {
    try {
      const module = await import(`@/locales/${lang}.json`);
      setTranslations(module.default ?? module);
    } catch {
      // fallback to empty
      setTranslations({});
    }
  }, []);

  useEffect(() => {
    const saved = (typeof window !== 'undefined' ? localStorage.getItem(LANG_KEY) : null) as Language | null;
    const initial: Language = saved === 'en' ? 'en' : 'it';
    setLanguageState(initial);
    loadTranslations(initial);
  }, [loadTranslations]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem(LANG_KEY, lang);
    }
    loadTranslations(lang);
  }, [loadTranslations]);

  const t = useCallback((key: string, fallback?: string): string => {
    const value = getNestedValue(translations as Record<string, unknown>, key);
    return value ?? fallback ?? key;
  }, [translations]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, translations }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export function useTranslation() {
  const { t, language } = useContext(LanguageContext);
  return { t, language };
}
